<template>
  <component
    :is="tag"
    :class="classes"
    class="button"
    :type="type"
    :disabled="isDisabled"
    :href="href"
    @click="handleClick"
  >
    <span v-if="loading" class="loading"><i class="icon icon-spinner" /></span>
    <span class="text"><slot /></span>
  </component>
</template>

<script>
export default {
  name: "CustomButton",
  props: {
    theme: {
      type: String,
      default: "primary",
      validator(value) {
        return [
          "primary",
          "secondary",
          "outline",
          "white",
          "underline",
        ].includes(value);
      },
    },
    type: {
      type: String,
      default: "button",
      validator(value) {
        return ["button", "submit"].includes(value);
      },
    },
    tag: {
      type: String,
      default: "button",
      validator(value) {
        return ["a", "button"].includes(value);
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    transparent: {
      type: Boolean,
      default: false,
    },
    light: {
      type: Boolean,
      default: false,
    },
    href: {
      type: String,
      default: null,
    },
    size: {
      type: String,
      default: "medium",
      validator(value) {
        return ["small", "medium", "large"].includes(value);
      },
    },
    easysize: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["click"],
  computed: {
    classes() {
      return {
        "button--small": this.size === "small",
        "button--medium": this.size === "medium",
        "button--large": this.size === "large",
        "button--full-width": this.fullWidth,
        "button--transparent": this.transparent,
        "button--underline": this.theme === "underline",
        "button--secondary": this.theme === "secondary",
        "button--white": this.theme === "white",
        "button--outline": this.theme === "outline",
        "button--light": this.light,
        "button--loading": this.loading,
        easysize_button: this.easysize,
      };
    },
    isDisabled() {
      return this.disabled || this.loading;
    },
  },
  methods: {
    handleClick() {
      this.$emit("click");
    },
  },
};
</script>

<style lang="scss" scoped>
.button {
  background: $color-black;
  padding: 14px;
  border-radius: 2px;
  outline: none;
  border: none;
  color: $color-white;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  @include t__utility-label-20;

  &--small {
    padding: 10px;
  }

  &--secondary {
    background-color: rgba($color-black, 0.3);
  }

  &--white {
    background-color: $color-white;
    color: $color-black;
  }

  &--outline {
    background-color: $color-white;
    border: 1px solid $color-black;
    color: $color-black;
  }
}

.button--full-width {
  display: block;
  width: 100%;
}

.button--transparent {
  background: transparent;
  padding-left: 0;
  padding-right: 0;
  color: $color-black;
}

.button--underline {
  padding: 4px 0;
  color: $color-black;
  background-color: transparent;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    left: 0;
    width: 100%;
    height: 1px;
    background: $color-black;
    bottom: 0;
    transition: width 0.5s ease-out;
  }
  &:hover::after {
    width: 0;
    transition: none;
  }
}

.button--light {
  color: $color-white;
}

.button--loading {
  position: relative;
  cursor: default;

  .loading {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font-size: 20px;
  }

  .text {
    opacity: 0.35;
  }
}

.icon.icon-spinner {
  transform-origin: center;
  display: inline-block;
  animation: spinner 0.75s infinite linear;
}

@keyframes spinner {
  100% {
    transform: rotate(360deg);
  }
}
</style>
